import React from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';

/**
 * AdminViewHeader Component
 * 
 * Simple, clean header showing which student's dashboard is being viewed in admin mode.
 * Focuses on the essential information without redundancy.
 * 
 * @param {Object} props - Component props
 * @param {StudentInfo|null} props.selectedStudent - Currently selected student information
 * @param {boolean} props.isAdminMode - Whether currently in admin mode
 * @returns {JSX.Element} AdminViewHeader component
 */
function AdminViewHeader({ 
  selectedStudent, 
  isAdminMode = true 
}) {
  // Context and permissions
  const { canAccessAdminDashboard } = useRoleAuthentication();

  // Don't render if not in admin mode, no permissions, or no selected student
  if (!isAdminMode || !canAccessAdminDashboard || !selectedStudent) {
    return null;
  }

  return (
    <Mui.Box
      role="banner"
      aria-label="Student dashboard admin view"
      sx={{
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
        borderLeft: '4px solid #1976d2',
        padding: '16px 24px',
        marginBottom: '16px',
        borderRadius: '0 4px 4px 0',
        border: '1px solid rgba(25, 118, 210, 0.2)',
      }}
    >
      <Mui.Container maxWidth="xl">
        <Mui.Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          {/* Student Information */}
          <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MuiIcons.Person 
              sx={{ 
                fontSize: '24px',
                color: 'primary.main'
              }}
              aria-hidden="true"
            />
            
            <Mui.Box>
              <Mui.Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: 1.2,
                  margin: 0,
                  color: 'text.primary'
                }}
              >
                {selectedStudent.name}
              </Mui.Typography>
              
              <Mui.Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: 'text.secondary',
                  lineHeight: 1.2
                }}
              >
                {selectedStudent.id} • {selectedStudent.email}
              </Mui.Typography>
              
              {/* Student Programs */}
              {selectedStudent.programs && selectedStudent.programs.length > 0 && (
                <Mui.Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selectedStudent.programs.map((program) => (
                    <Mui.Chip
                      key={program}
                      label={program}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '11px',
                        height: '20px',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '& .MuiChip-label': {
                          padding: '0 6px'
                        }
                      }}
                    />
                  ))}
                </Mui.Box>
              )}
            </Mui.Box>
          </Mui.Box>

          {/* Read-Only Indicator */}
          <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Mui.Tooltip 
              title="You are viewing this dashboard in read-only mode. No actions can be performed."
              arrow
            >
              <Mui.Chip
                icon={<MuiIcons.Lock />}
                label="Admin View • Read-Only"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'warning.main',
                  color: 'warning.main',
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  '& .MuiChip-icon': {
                    color: 'warning.main'
                  }
                }}
                aria-label="Read-only admin mode indicator"
              />
            </Mui.Tooltip>
          </Mui.Box>
        </Mui.Box>
      </Mui.Container>
    </Mui.Box>
  );
}

export default AdminViewHeader;